import { type FC, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NavbarSidebarLayout from "../layouts/navbar-sidebar";
import {
  Breadcrumb,
  Button,
  Card,
  Label,
  TextInput,
  Textarea,
  Select,
  Spinner,
  Checkbox,
  Modal,
  Alert,
  Badge,
} from "flowbite-react";
import {
  HiHome,
  HiSave,
  HiX,
  HiPlus,
  HiChevronLeft,
  HiChevronRight,
  HiInformationCircle,
  HiTemplate,
  HiCheckCircle,
  HiChartBar,
} from "react-icons/hi";
import { FiMessageCircle, FiPhone } from "react-icons/fi";
import { campaignService, companyService, userService, contactService } from "../services/main";
import { SegmentBuilder } from "../components/segments/SegmentBuilder";
import { apiWithAuth } from "../services/utils";
import toast from "react-hot-toast";

type Message = {
  channel: "EMAIL" | "SMS" | "WHATSAPP" | "TELEPHONY";
  subject?: string;
  body: string;
};

type ScheduleConfig = {
  frequency?: "DAILY" | "WEEKLY" | "MONTHLY";
  interval?: number;
  daysOfWeek?: number[];
  endDate?: string;
  triggerEvent?: string;
};

type Template = {
  id: string;
  name: string;
  description: string;
  messages: Message[];
};

const PREDEFINED_TEMPLATES: Template[] = [
  {
    id: "welcome",
    name: "Welcome Campaign",
    description: "Welcome new contacts with a friendly introduction",
    messages: [
      {
        channel: "EMAIL",
        subject: "Welcome to Our Platform!",
        body: "Hi {{firstName}},\n\nWelcome! We're excited to have you on board. Get started by exploring our features.\n\nBest regards,\nThe Team",
      },
    ],
  },
  {
    id: "follow-up",
    name: "Follow-up Campaign",
    description: "Follow up with contacts after initial interaction",
    messages: [
      {
        channel: "EMAIL",
        subject: "Following Up - {{companyName}}",
        body: "Hi {{firstName}},\n\nI wanted to follow up on our recent conversation. Let me know if you have any questions.\n\nBest regards,\n{{yourName}}",
      },
    ],
  },
  {
    id: "promotion",
    name: "Promotional Campaign",
    description: "Share special offers and promotions",
    messages: [
      {
        channel: "EMAIL",
        subject: "Special Offer Just for You!",
        body: "Hi {{firstName}},\n\nWe have an exclusive offer for you! Use code PROMO2024 for 20% off.\n\nDon't miss out!\n\nThe Team",
      },
    ],
  },
  {
    id: "reminder",
    name: "Reminder Campaign",
    description: "Send reminders for important dates or tasks",
    messages: [
      {
        channel: "EMAIL",
        subject: "Reminder: {{eventName}}",
        body: "Hi {{firstName}},\n\nThis is a friendly reminder about {{eventName}} scheduled for {{date}}.\n\nSee you there!\n\nThe Team",
      },
    ],
  },
];

const STEPS = [
  { id: 1, name: "Basic Information", description: "Campaign name and description" },
  { id: 2, name: "Campaign Settings", description: "Send type, target, and scheduling" },
  { id: 3, name: "Messages", description: "Create your campaign messages" },
  { id: 4, name: "Review", description: "Review and submit" },
];

const CreateCampaignPage: FC = function () {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [segments, setSegments] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [newSegmentName, setNewSegmentName] = useState("");
  const [newSegmentDescription, setNewSegmentDescription] = useState("");
  const [newSegmentFilterRules, setNewSegmentFilterRules] = useState<any>({ groups: [], logic: "AND" });
  const [creatingSegment, setCreatingSegment] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [estimatedReach, setEstimatedReach] = useState<number | null>(null);
  const [loadingReach, setLoadingReach] = useState(false);
  const [totalContacts, setTotalContacts] = useState<number>(0);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    companyId: "",
    sendType: "NOW" as "NOW" | "SCHEDULED" | "RECURRING" | "AUTOMATION",
    targetType: "ALL_COMPANIES" as "SPECIFIC_COMPANY" | "ALL_COMPANIES" | "SEGMENT",
    targetCompanyId: "",
    scheduledAt: "",
    segmentId: "",
    messages: [{ channel: "EMAIL" as const, subject: "", body: "" }] as Message[],
    attachmentUrl: "",
    scheduleConfig: {} as ScheduleConfig,
  });

  useEffect(() => {
    fetchSegments();
    fetchCompanies();
    fetchUsers();
    fetchTotalContacts();
  }, []);

  const fetchTotalContacts = async () => {
    try {
      const response = await contactService.listContacts();
      const contacts = response.data?.contacts || [];
      setTotalContacts(contacts.length);
    } catch (error) {
      console.error("Failed to fetch total contacts:", error);
    }
  };

  useEffect(() => {
    const calculateEstimatedReach = async () => {
      if (formData.targetType === "ALL_COMPANIES") {
        setEstimatedReach(totalContacts);
        return;
      }

      if (formData.targetType === "SPECIFIC_COMPANY" && formData.targetCompanyId) {
        setLoadingReach(true);
        try {
          const response = await contactService.listContacts({ companyId: formData.targetCompanyId });
          const contacts = response.data?.contacts || [];
          setEstimatedReach(contacts.length);
        } catch (error) {
          console.error("Failed to fetch company contacts:", error);
          setEstimatedReach(null);
        } finally {
          setLoadingReach(false);
        }
        return;
      }

      if (formData.targetType === "SEGMENT" && formData.segmentId) {
        setLoadingReach(true);
        try {
          // Call API to get estimated reach for the segment
          const response = await apiWithAuth.get(`/segments/${formData.segmentId}/estimate`);
          setEstimatedReach(response.data?.estimatedReach || 0);
        } catch (error) {
          console.error("Failed to calculate segment reach:", error);
          setEstimatedReach(null);
        } finally {
          setLoadingReach(false);
        }
        return;
      }

      setEstimatedReach(null);
    };

    calculateEstimatedReach();
  }, [formData.targetType, formData.targetCompanyId, formData.segmentId, totalContacts, segments]);

  const fetchUsers = async () => {
    try {
      const response = await userService.getAllUsers();
      setUsers(response.data?.users || response.data || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const fetchSegments = async () => {
    try {
      setLoadingSegments(true);
      const response = await apiWithAuth.get("/segments");
      setSegments(response.data?.segments || []);
    } catch (error) {
      console.error("Failed to fetch segments:", error);
    } finally {
      setLoadingSegments(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await companyService.listCompanies();
      setCompanies(response.data?.companies || []);
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleCreateSegment = async () => {
    if (!newSegmentName.trim()) {
      toast.error("Segment name is required");
      return;
    }

    // Validate that if groups exist, they have at least one valid rule
    const hasValidRules = newSegmentFilterRules.groups.length === 0 || 
      newSegmentFilterRules.groups.some(g => g.rules.some(r => r.field && r.operator && r.value !== "" && r.value !== null));
    
    if (!hasValidRules && newSegmentFilterRules.groups.length > 0) {
      toast.error("Please complete at least one filter rule or remove empty groups");
      return;
    }

    try {
      setCreatingSegment(true);
      const response = await apiWithAuth.post("/segments", {
        name: newSegmentName,
        description: newSegmentDescription || undefined,
        filterRules: newSegmentFilterRules.groups.length === 0 ? {} : newSegmentFilterRules,
        isActive: true,
        companyId: formData.companyId ? parseInt(formData.companyId) : undefined,
      });
      await fetchSegments();
      setFormData((prev) => ({ ...prev, segmentId: response.data.segment.id.toString() }));
      setShowSegmentModal(false);
      setNewSegmentName("");
      setNewSegmentDescription("");
      setNewSegmentFilterRules({ groups: [], logic: "AND" });
      toast.success("Segment created successfully!");
    } catch (error: any) {
      console.error("Failed to create segment:", error);
      toast.error(error.response?.data?.message || "Failed to create segment");
    } finally {
      setCreatingSegment(false);
    }
  };

  const handleTemplateSelect = (template: Template) => {
    setFormData((prev) => ({
      ...prev,
      messages: template.messages.map((msg) => ({ ...msg })),
    }));
    setShowTemplateModal(false);
    toast.success(`Template "${template.name}" applied!`);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleScheduleConfigChange = (field: keyof ScheduleConfig, value: any) => {
    setFormData((prev) => ({
      ...prev,
      scheduleConfig: {
        ...prev.scheduleConfig,
        [field]: value,
      },
    }));
  };

  const handleMessageChange = (index: number, field: keyof Message, value: string) => {
    setFormData((prev) => {
      const newMessages = [...prev.messages];
      newMessages[index] = { ...newMessages[index], [field]: value };
      return { ...prev, messages: newMessages };
    });
  };

  const addMessage = () => {
    setFormData((prev) => ({
      ...prev,
      messages: [...prev.messages, { channel: "EMAIL", subject: "", body: "" }],
    }));
  };

  const removeMessage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      messages: prev.messages.filter((_, i) => i !== index),
    }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        if (!formData.name.trim()) {
          toast.error("Campaign name is required");
          return false;
        }
        return true;
      case 2:
        if (formData.sendType === "SCHEDULED" && !formData.scheduledAt) {
          toast.error("Scheduled date is required for scheduled campaigns");
          return false;
        }
        if (formData.sendType === "RECURRING") {
          if (!formData.scheduleConfig.frequency) {
            toast.error("Frequency is required for recurring campaigns");
            return false;
          }
          if (!formData.scheduleConfig.interval) {
            toast.error("Interval is required for recurring campaigns");
            return false;
          }
        }
        if (formData.sendType === "AUTOMATION" && !formData.scheduleConfig.triggerEvent) {
          toast.error("Trigger event is required for automation campaigns");
          return false;
        }
        if (formData.targetType === "SPECIFIC_COMPANY" && !formData.targetCompanyId) {
          toast.error("Please select a company for specific company targeting");
          return false;
        }
        if (formData.targetType === "SEGMENT" && !formData.segmentId) {
          toast.error("Please select or create a segment for custom audience targeting");
          return false;
        }
        return true;
      case 3:
        if (formData.messages.length === 0) {
          toast.error("At least one message is required");
          return false;
        }
        // WhatsApp messages don't require body (handled by WhatsApp client)
        if (formData.messages.some((msg) => msg.channel !== "WHATSAPP" && !msg.body.trim())) {
          toast.error("All messages must have a body (except WhatsApp)");
          return false;
        }
        if (formData.messages.some((msg) => msg.channel === "EMAIL" && !msg.subject?.trim())) {
          toast.error("Email messages must have a subject");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep(3)) {
      setCurrentStep(3);
      return;
    }

    try {
      setLoading(true);
      const payload: any = {
        name: formData.name,
        description: formData.description || undefined,
        sendType: formData.sendType,
        targetType: formData.targetType,
        messages: formData.messages.map((msg) => ({
          channel: msg.channel,
          subject: msg.subject || undefined,
          body: msg.body,
        })),
        status: "DRAFT",
      };

      // Handle target-specific company
      if (formData.targetType === "SPECIFIC_COMPANY" && formData.targetCompanyId) {
        payload.companyId = parseInt(formData.targetCompanyId);
      }

      // Handle segment targeting
      if (formData.targetType === "SEGMENT" && formData.segmentId) {
        payload.segmentId = parseInt(formData.segmentId);
      }

      if (formData.sendType === "SCHEDULED" && formData.scheduledAt) {
        payload.scheduledAt = new Date(formData.scheduledAt).toISOString();
      }

      if (formData.sendType === "RECURRING" || formData.sendType === "AUTOMATION") {
        const scheduleConfig: any = {};
        if (formData.sendType === "RECURRING") {
          scheduleConfig.frequency = formData.scheduleConfig.frequency;
          scheduleConfig.interval = formData.scheduleConfig.interval;
          if (formData.scheduleConfig.daysOfWeek && formData.scheduleConfig.daysOfWeek.length > 0) {
            scheduleConfig.daysOfWeek = formData.scheduleConfig.daysOfWeek;
          }
          if (formData.scheduleConfig.endDate) {
            scheduleConfig.endDate = new Date(formData.scheduleConfig.endDate).toISOString();
          }
        }
        if (formData.sendType === "AUTOMATION") {
          scheduleConfig.triggerEvent = formData.scheduleConfig.triggerEvent;
        }
        if (Object.keys(scheduleConfig).length > 0) {
          payload.scheduleConfig = scheduleConfig;
        }
      }

      if (formData.attachmentUrl) {
        payload.attachmentUrl = formData.attachmentUrl;
      }

      await campaignService.createCampaign(payload);
      toast.success("Campaign created successfully!");
      navigate("/pages/campaigns");
    } catch (error: any) {
      console.error("Failed to create campaign:", error);
      toast.error(error.response?.data?.message || "Failed to create campaign");
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="name" className="mb-2 block">
                Campaign Name *
              </Label>
              <TextInput
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter campaign name"
                required
                className="w-full"
              />
            </div>

            <div>
              <Label htmlFor="description" className="mb-2 block">
                Description
              </Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter campaign description"
                rows={4}
                className="w-full"
              />
            </div>

          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <Label htmlFor="sendType" className="mb-2 block">
                Send Type *
              </Label>
              <Select id="sendType" name="sendType" value={formData.sendType} onChange={handleChange} required>
                <option value="NOW">Send Now</option>
                <option value="SCHEDULED">Scheduled</option>
                <option value="RECURRING">Recurring</option>
                <option value="AUTOMATION">Automation</option>
              </Select>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="targetType" className="mb-2 block text-base font-semibold">
                  Target Audience *
                </Label>
                <Select
                  id="targetType"
                  name="targetType"
                  value={formData.targetType}
                  onChange={handleChange}
                  required
                >
                  <option value="ALL_COMPANIES">All Companies (All contacts from all companies)</option>
                  <option value="SPECIFIC_COMPANY">Specific Company (Contacts from selected company)</option>
                  <option value="SEGMENT">Custom Segment (Use advanced filters to create custom audience)</option>
                </Select>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {formData.targetType === "ALL_COMPANIES" && (
                    <span className="flex items-start gap-2">
                      <HiInformationCircle className="text-blue-500 mt-0.5 flex-shrink-0" />
                      <span>This campaign will target all contacts from all companies in your CRM.</span>
                    </span>
                  )}
                  {formData.targetType === "SPECIFIC_COMPANY" && (
                    <span className="flex items-start gap-2">
                      <HiInformationCircle className="text-blue-500 mt-0.5 flex-shrink-0" />
                      <span>Select a specific company below. The campaign will target all contacts associated with that company.</span>
                    </span>
                  )}
                  {formData.targetType === "SEGMENT" && (
                    <span className="flex items-start gap-2">
                      <HiInformationCircle className="text-blue-500 mt-0.5 flex-shrink-0" />
                      <span>Use segments to create custom audiences with advanced filtering rules. Create a new segment or select an existing one below.</span>
                    </span>
                  )}
                </p>
              </div>

              {formData.targetType === "SPECIFIC_COMPANY" && (
                <div>
                  <Label htmlFor="targetCompanyId" className="mb-2 block">
                    Select Company *
                  </Label>
                  {loadingCompanies ? (
                    <Spinner size="sm" />
                  ) : (
                    <Select
                      id="targetCompanyId"
                      name="targetCompanyId"
                      value={formData.targetCompanyId}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Choose a company...</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </Select>
                  )}
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    All contacts associated with the selected company will be targeted
                  </p>
                </div>
              )}
            </div>

            {formData.sendType === "SCHEDULED" && (
              <div>
                <Label htmlFor="scheduledAt" className="mb-2 block">
                  Scheduled Date & Time *
                </Label>
                <TextInput
                  id="scheduledAt"
                  name="scheduledAt"
                  type="datetime-local"
                  value={formData.scheduledAt}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {formData.sendType === "RECURRING" && (
              <div className="space-y-4 border-t pt-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recurrence Settings</h3>
                <div>
                  <Label htmlFor="frequency" className="mb-2 block">
                    Frequency *
                  </Label>
                  <Select
                    id="frequency"
                    value={formData.scheduleConfig.frequency || ""}
                    onChange={(e) => handleScheduleConfigChange("frequency", e.target.value)}
                    required
                  >
                    <option value="">Select Frequency</option>
                    <option value="DAILY">Daily</option>
                    <option value="WEEKLY">Weekly</option>
                    <option value="MONTHLY">Monthly</option>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="interval" className="mb-2 block">
                    Interval (Every N {formData.scheduleConfig.frequency?.toLowerCase() || "period"}) *
                  </Label>
                  <TextInput
                    id="interval"
                    type="number"
                    min="1"
                    value={formData.scheduleConfig.interval || ""}
                    onChange={(e) => handleScheduleConfigChange("interval", parseInt(e.target.value) || 1)}
                    placeholder="e.g., 1"
                    required
                  />
                </div>

                {formData.scheduleConfig.frequency === "WEEKLY" && (
                  <div>
                    <Label className="mb-2 block">Days of Week</Label>
                    <div className="space-y-2">
                      {[
                        { value: 0, label: "Sunday" },
                        { value: 1, label: "Monday" },
                        { value: 2, label: "Tuesday" },
                        { value: 3, label: "Wednesday" },
                        { value: 4, label: "Thursday" },
                        { value: 5, label: "Friday" },
                        { value: 6, label: "Saturday" },
                      ].map((day) => (
                        <Checkbox
                          key={day.value}
                          id={`day-${day.value}`}
                          checked={formData.scheduleConfig.daysOfWeek?.includes(day.value) || false}
                          onChange={(e) => {
                            const currentDays = formData.scheduleConfig.daysOfWeek || [];
                            if (e.target.checked) {
                              handleScheduleConfigChange("daysOfWeek", [...currentDays, day.value]);
                            } else {
                              handleScheduleConfigChange(
                                "daysOfWeek",
                                currentDays.filter((d) => d !== day.value)
                              );
                            }
                          }}
                          label={day.label}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="endDate" className="mb-2 block">
                    End Date (Optional)
                  </Label>
                  <TextInput
                    id="endDate"
                    type="date"
                    value={formData.scheduleConfig.endDate || ""}
                    onChange={(e) => handleScheduleConfigChange("endDate", e.target.value)}
                  />
                </div>
              </div>
            )}

            {formData.sendType === "AUTOMATION" && (
              <div className="space-y-4 border-t pt-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Automation Settings</h3>
                <div>
                  <Label htmlFor="triggerEvent" className="mb-2 block">
                    Trigger Event *
                  </Label>
                  <Select
                    id="triggerEvent"
                    value={formData.scheduleConfig.triggerEvent || ""}
                    onChange={(e) => handleScheduleConfigChange("triggerEvent", e.target.value)}
                    required
                  >
                    <option value="">Select Trigger Event</option>
                    <option value="CONTACT_CREATED">Contact Created</option>
                    <option value="DEAL_WON">Deal Won</option>
                    <option value="DEAL_LOST">Deal Lost</option>
                    <option value="COMPANY_CREATED">Company Created</option>
                    <option value="CUSTOM">Custom Event</option>
                  </Select>
                </div>
              </div>
            )}

            {formData.targetType === "SEGMENT" && (
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="segmentId" className="mb-0 text-base font-semibold">
                    Select Segment *
                  </Label>
                  <Button
                    type="button"
                    size="xs"
                    color="primary"
                    onClick={() => setShowSegmentModal(true)}
                  >
                    <HiPlus className="mr-1" />
                    Create New Segment
                  </Button>
                </div>
                {loadingSegments ? (
                  <Spinner size="sm" />
                ) : segments.length === 0 ? (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                      No segments found. Create your first segment to target a custom audience.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      color="primary"
                      onClick={() => setShowSegmentModal(true)}
                    >
                      <HiPlus className="mr-2" />
                      Create Segment
                    </Button>
                  </div>
                ) : (
                  <Select id="segmentId" name="segmentId" value={formData.segmentId} onChange={handleChange} required>
                    <option value="">Choose a segment...</option>
                    {segments.map((segment) => (
                      <option key={segment.id} value={segment.id}>
                        {segment.name} {segment.description && `- ${segment.description}`}
                      </option>
                    ))}
                  </Select>
                )}
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Segments allow you to create custom audiences using advanced filters based on company, contact, and deal criteria.
                </p>
              </div>
            )}

            {/* Estimated Reach Preview */}
            {(formData.targetType === "ALL_COMPANIES" || formData.targetType === "SPECIFIC_COMPANY" || (formData.targetType === "SEGMENT" && formData.segmentId)) && (
              <div className="border-t pt-4 mt-4">
                <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 border-2 border-blue-200 dark:border-blue-700">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <HiChartBar className="text-2xl text-blue-600 dark:text-blue-400" />
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Estimated Reach</h4>
                    </div>
                    {loadingReach ? (
                      <div className="flex items-center gap-2">
                        <Spinner size="sm" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Calculating audience size...</span>
                      </div>
                    ) : estimatedReach !== null ? (
                      <div className="space-y-2">
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-bold text-blue-600 dark:text-blue-400">
                            {estimatedReach.toLocaleString()}
                          </span>
                          <span className="text-lg text-gray-600 dark:text-gray-400">contacts</span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {formData.targetType === "ALL_COMPANIES" && "All contacts from all companies will be targeted"}
                          {formData.targetType === "SPECIFIC_COMPANY" && formData.targetCompanyId && 
                            `Contacts from ${companies.find((c) => c.id === parseInt(formData.targetCompanyId))?.name || "selected company"} will be targeted`}
                          {formData.targetType === "SEGMENT" && formData.segmentId && 
                            `Contacts matching the selected segment criteria will be targeted`}
                        </p>
                        <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Estimated Delivery</span>
                              <p className="font-semibold text-gray-900 dark:text-white mt-1">
                                {estimatedReach > 0 ? "Ready to send" : "No contacts found"}
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Campaign Type</span>
                              <p className="font-semibold text-gray-900 dark:text-white mt-1">
                                {formData.messages.length} message{formData.messages.length !== 1 ? "s" : ""}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {formData.targetType === "SEGMENT" && !formData.segmentId
                          ? "Select a segment to see estimated reach"
                          : "Unable to calculate estimated reach"}
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Campaign Messages</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Create messages for different channels
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="button" color="gray" size="sm" onClick={() => setShowTemplateModal(true)}>
                  <HiTemplate className="mr-2" />
                  Templates
                </Button>
                <Button type="button" color="primary" size="sm" onClick={addMessage}>
                  <HiPlus className="mr-2" />
                  Add Message
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              {formData.messages.map((message, index) => (
                <Card key={index} className="relative border-2 border-gray-200 dark:border-gray-700">
                  {formData.messages.length > 1 && (
                    <Button
                      type="button"
                      color="failure"
                      size="xs"
                      className="absolute top-2 right-2"
                      onClick={() => removeMessage(index)}
                    >
                      <HiX />
                    </Button>
                  )}
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor={`channel-${index}`} className="mb-2 block">
                        Channel *
                      </Label>
                      <Select
                        id={`channel-${index}`}
                        value={message.channel}
                        onChange={(e) =>
                          handleMessageChange(index, "channel", e.target.value as Message["channel"])
                        }
                        required
                      >
                        <option value="EMAIL">Email</option>
                        <option value="SMS">SMS</option>
                        <option value="WHATSAPP">WhatsApp</option>
                        <option value="TELEPHONY">Telephony</option>
                      </Select>
                      {(message.channel === "WHATSAPP" || message.channel === "TELEPHONY") && (
                        <Alert color="info" className="mt-2" icon={HiInformationCircle}>
                          <div className="flex items-start gap-2">
                            {message.channel === "WHATSAPP" ? (
                              <FiMessageCircle className="text-lg mt-0.5" />
                            ) : (
                              <FiPhone className="text-lg mt-0.5" />
                            )}
                            <div>
                              <p className="font-semibold">
                                {message.channel === "WHATSAPP" ? "WhatsApp" : "Telephony"} Device Required
                              </p>
                              <p className="text-sm mt-1">
                                {message.channel === "WHATSAPP"
                                  ? "Make sure you have connected a WhatsApp device in the Devices section before sending this campaign. Campaigns will only be sent when a device is online and connected."
                                  : "Make sure you have connected a Telephony device in the Devices section before sending this campaign. Campaigns will only be sent when a device is online and connected."}
                              </p>
                            </div>
                          </div>
                        </Alert>
                      )}
                    </div>

                    {(message.channel === "EMAIL" || message.channel === "WHATSAPP") && (
                      <div>
                        <Label htmlFor={`subject-${index}`} className="mb-2 block">
                          Subject {message.channel === "EMAIL" ? "*" : "(Optional)"}
                        </Label>
                        <TextInput
                          id={`subject-${index}`}
                          value={message.subject || ""}
                          onChange={(e) => handleMessageChange(index, "subject", e.target.value)}
                          placeholder="Enter message subject"
                          required={message.channel === "EMAIL"}
                        />
                      </div>
                    )}

                    <div>
                      <Label htmlFor={`body-${index}`} className="mb-2 block">
                        Message Body {message.channel === "WHATSAPP" ? "(Optional - handled by WhatsApp client)" : "*"}
                      </Label>
                      <Textarea
                        id={`body-${index}`}
                        value={message.body}
                        onChange={(e) => handleMessageChange(index, "body", e.target.value)}
                        placeholder={message.channel === "WHATSAPP" 
                          ? "Message template is optional - WhatsApp client will handle message composition"
                          : "Enter message body. You can use variables like {{firstName}}, {{lastName}}, {{companyName}}"}
                        rows={6}
                        required={message.channel !== "WHATSAPP"}
                        className="font-mono text-sm"
                      />
                      {message.channel !== "WHATSAPP" && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Available variables: {`{{firstName}}, {{lastName}}, {{companyName}}, {{email}}`}
                        </p>
                      )}
                      {message.channel === "WHATSAPP" && (
                        <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                          Note: Message templates for WhatsApp are handled by the WhatsApp client. You can leave this empty.
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div>
              <Label htmlFor="attachmentUrl" className="mb-2 block">
                Attachment URL (Optional)
              </Label>
              <TextInput
                id="attachmentUrl"
                name="attachmentUrl"
                value={formData.attachmentUrl}
                onChange={handleChange}
                placeholder="https://example.com/file.pdf"
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Campaign Summary</h3>

            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-500 dark:text-gray-400">Campaign Name</Label>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">{formData.name}</p>
                  </div>

                  {formData.description && (
                    <div>
                      <Label className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</Label>
                      <p className="text-gray-900 dark:text-white mt-1">{formData.description}</p>
                    </div>
                  )}


                  <div>
                    <Label className="text-sm font-medium text-gray-500 dark:text-gray-400">Send Type</Label>
                    <Badge color="info" className="mt-1">
                      {formData.sendType}
                    </Badge>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-500 dark:text-gray-400">Target Audience</Label>
                    <div className="mt-1 space-y-1">
                      <Badge color="success" className="mr-2">
                        {formData.targetType === "ALL_COMPANIES" && "All Companies"}
                        {formData.targetType === "SPECIFIC_COMPANY" && "Specific Company"}
                        {formData.targetType === "SEGMENT" && "Custom Segment"}
                      </Badge>
                      {formData.targetType === "SPECIFIC_COMPANY" && formData.targetCompanyId && (
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {companies.find((c) => c.id === parseInt(formData.targetCompanyId))?.name || "N/A"}
                        </p>
                      )}
                      {formData.targetType === "SEGMENT" && formData.segmentId && (
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {segments.find((s) => s.id === parseInt(formData.segmentId))?.name || "N/A"}
                        </p>
                      )}
                    </div>
                  </div>

                  {formData.sendType === "SCHEDULED" && formData.scheduledAt && (
                    <div>
                      <Label className="text-sm font-medium text-gray-500 dark:text-gray-400">Scheduled At</Label>
                      <p className="text-gray-900 dark:text-white mt-1">
                        {new Date(formData.scheduledAt).toLocaleString()}
                      </p>
                    </div>
                  )}

                  {formData.sendType === "RECURRING" && (
                    <div>
                      <Label className="text-sm font-medium text-gray-500 dark:text-gray-400">Recurrence</Label>
                      <p className="text-gray-900 dark:text-white mt-1">
                        Every {formData.scheduleConfig.interval} {formData.scheduleConfig.frequency?.toLowerCase()}
                        {formData.scheduleConfig.daysOfWeek &&
                          formData.scheduleConfig.daysOfWeek.length > 0 &&
                          ` on ${formData.scheduleConfig.daysOfWeek.map((d) => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d]).join(", ")}`}
                      </p>
                    </div>
                  )}

                  {formData.segmentId && (
                    <div>
                      <Label className="text-sm font-medium text-gray-500 dark:text-gray-400">Segment</Label>
                      <p className="text-gray-900 dark:text-white mt-1">
                        {segments.find((s) => s.id === parseInt(formData.segmentId))?.name || "N/A"}
                      </p>
                    </div>
                  )}
                </div>

                <div className="border-t pt-4">
                  <Label className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 block">
                    Messages ({formData.messages.length})
                  </Label>
                  <div className="space-y-2">
                    {formData.messages.map((msg, idx) => (
                      <Card key={idx} className="bg-white dark:bg-gray-800">
                        <div className="flex items-start gap-3">
                          <Badge color={msg.channel === "EMAIL" ? "info" : msg.channel === "SMS" ? "warning" : "success"}>
                            {msg.channel}
                          </Badge>
                          <div className="flex-1">
                            {msg.subject && (
                              <p className="font-semibold text-gray-900 dark:text-white">{msg.subject}</p>
                            )}
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{msg.body}</p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <NavbarSidebarLayout>
      <div className="px-4 pt-6 pb-8">
        <Breadcrumb className="mb-4">
          <Breadcrumb.Item onClick={() => navigate("/")}>
            <div className="flex items-center gap-x-3 cursor-pointer">
              <HiHome className="text-xl" />
              <span className="dark:text-white">Home</span>
            </div>
          </Breadcrumb.Item>
          <Breadcrumb.Item onClick={() => navigate("/pages/campaigns")}>
            <span className="cursor-pointer">Campaigns</span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Create Campaign</Breadcrumb.Item>
        </Breadcrumb>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Create Campaign</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Create a new marketing campaign with multi-channel messaging
          </p>
        </div>

        {/* Step Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all ${
                      currentStep > step.id
                        ? "bg-green-500 text-white shadow-lg"
                        : currentStep === step.id
                        ? "bg-blue-600 text-white shadow-lg ring-4 ring-blue-200 dark:ring-blue-800"
                        : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                    }`}
                  >
                    {currentStep > step.id ? <HiCheckCircle className="text-2xl" /> : step.id}
                  </div>
                  <div className="mt-3 text-center">
                    <p
                      className={`text-sm font-semibold ${
                        currentStep >= step.id
                          ? "text-gray-900 dark:text-white"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {step.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{step.description}</p>
                  </div>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-4 rounded transition-all ${
                      currentStep > step.id
                        ? "bg-green-500"
                        : "bg-gray-200 dark:bg-gray-700"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="mb-6 shadow-lg border-2 border-gray-200 dark:border-gray-700">
            <div className="min-h-[500px] p-6">{renderStepContent()}</div>
          </Card>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between">
            <Button
              type="button"
              color="gray"
              onClick={currentStep === 1 ? () => navigate("/pages/campaigns") : handlePrevious}
              disabled={loading}
            >
              <HiChevronLeft className="mr-2" />
              {currentStep === 1 ? "Cancel" : "Previous"}
            </Button>

            <div className="flex gap-4">
              {currentStep < STEPS.length ? (
                <Button type="button" color="primary" onClick={handleNext} disabled={loading}>
                  Next
                  <HiChevronRight className="ml-2" />
                </Button>
              ) : (
                <Button type="submit" color="primary" disabled={loading} className="px-6">
                  {loading ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <HiSave className="mr-2" />
                      Create Campaign
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </form>

        {/* Create Segment Modal */}
        <Modal show={showSegmentModal} onClose={() => {
          setShowSegmentModal(false);
          setNewSegmentName("");
          setNewSegmentDescription("");
          setNewSegmentFilterRules({ groups: [], logic: "AND" });
        }} size="6xl">
          <Modal.Header className="border-b border-gray-200 !p-4 dark:border-gray-700">
            <strong>Create New Segment</strong>
          </Modal.Header>
          <Modal.Body className="max-h-[70vh] overflow-y-auto">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="segmentName" className="mb-1 block text-sm">
                    Segment Name *
                  </Label>
                  <TextInput
                    id="segmentName"
                    value={newSegmentName}
                    onChange={(e) => setNewSegmentName(e.target.value)}
                    placeholder="e.g., Tech Companies in NYC"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="segmentDescription" className="mb-1 block text-sm">
                    Description (Optional)
                  </Label>
                  <TextInput
                    id="segmentDescription"
                    value={newSegmentDescription}
                    onChange={(e) => setNewSegmentDescription(e.target.value)}
                    placeholder="Brief description"
                  />
                </div>
              </div>
              
              <div className="border-t pt-3">
                <SegmentBuilder
                  filterRules={newSegmentFilterRules}
                  onChange={setNewSegmentFilterRules}
                  companies={companies}
                  users={users}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button color="gray" onClick={() => {
              setShowSegmentModal(false);
              setNewSegmentName("");
              setNewSegmentDescription("");
              setNewSegmentFilterRules({ groups: [], logic: "AND" });
            }}>
              Cancel
            </Button>
            <Button 
              color="primary" 
              onClick={handleCreateSegment} 
              disabled={creatingSegment || !newSegmentName.trim()}
            >
              {creatingSegment ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <HiCheckCircle className="mr-2" />
                  Create Segment
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Template Selection Modal */}
        <Modal show={showTemplateModal} onClose={() => setShowTemplateModal(false)} size="xl">
          <Modal.Header>Select a Template</Modal.Header>
          <Modal.Body>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PREDEFINED_TEMPLATES.map((template) => (
                <Card
                  key={template.id}
                  className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-blue-500"
                  onClick={() => handleTemplateSelect(template)}
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                      <HiTemplate className="text-2xl text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white">{template.name}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{template.description}</p>
                      <Badge color="info" className="mt-2">
                        {template.messages.length} message{template.messages.length > 1 ? "s" : ""}
                      </Badge>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button color="gray" onClick={() => setShowTemplateModal(false)}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </NavbarSidebarLayout>
  );
};

export default CreateCampaignPage;
